import React, { useEffect, useState } from "react";
import shortUUID from "short-uuid";
import Select from "react-select";
import { PlusIcon } from "../../assets/Icons";
import apiClient from "../../apiClient";
import { SSO_URL } from "../../env";
import {
  FETCH_APPLICATION_TEMPLATE,
  FETCH_ENUM_TYPES,
  SAVE_DEFAULT_CONFIGURATION,
  UPDATE_CONFIGURATION_VARIABLES,
  UPDATE_ENVIRONMENT_VARIABLES,
} from "../../apiClient/apiEndpoints";
import { useParams } from "react-router-dom";
import { getValueType } from "../../utils/general";

const generateShortUUID = () => shortUUID.generate();

const parseBoolean = (str) => (str.toLowerCase() === "true" ? true : false);

const parseValue = (str, dataType) => {
  if (dataType === "string") {
    return str.trim();
  }
  if (dataType === "number") {
    return Number(str.trim());
  }
  if (dataType === "boolean") {
    return parseBoolean(str.trim());
  }
  if (dataType === "object") {
    return JSON.parse(str.trim());
  }
  if (dataType === "null") {
    return null;
  }
};

const isValidJson = (str) => {
  try {
    if (typeof str !== "string") return false;
    const trimmed = str.trim();
    if (trimmed === "") return false;
    JSON.parse(trimmed);
    return true;
  } catch (error) {
    return false;
  }
};

function isValidNumber(str) {
  if (typeof str !== "string") return false;
  const trimmed = str.trim();
  return trimmed !== "" && !isNaN(trimmed) && isFinite(Number(trimmed));
}

const isValidBoolean = (str) => {
  if (typeof str !== "string") return false;
  const trimmed = str.trim();
  return trimmed !== "" && (trimmed.toLowerCase() === "true" || trimmed.toLowerCase() === "false");
};

const validateValue = (value, dataType) => {
  if (dataType === "number") {
    return isValidNumber(value);
  }
  if (dataType === "boolean") {
    return isValidBoolean(value);
  }
  if (dataType === "object") {
    return isValidJson(value);
  }
  if (dataType === "null") {
    return value === "null";
  }
  return true;
};

const dataTypeOptions = [
  {
    label: "String",
    value: "string",
  },
  {
    label: "Number",
    value: "number",
  },
  {
    label: "Boolean",
    value: "boolean",
  },
  {
    label: "Object",
    value: "object",
  },
  {
    label: "Null",
    value: "null",
  },
];

const templateTypeOptions = [
  {
    label: "Application",
    value: "APPLICATION",
  },
  {
    label: "Channel",
    value: "CHANNEL",
  },
];

const getValueToPopulate = (value) => {
  const valueType = getValueType(value);
  if (valueType === "object") {
    return JSON.stringify(value || {});
  }
  if (valueType === "null") {
    return "null";
  }
  return value;
};

const UpdateApplicationConfig = ({
  applicationId,
  defaultConfig,
  configData,
  defaultConfigMode,
  isEnv = false,
  isDirectory = false,
  reRender = () => {},
}) => {
  const [dataList, setDataList] = useState([]);
  const [configName, setConfigName] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [configurationKeys, setConfigurationKeys] = useState([]);
  const [configNameOptions, setConfigNameOptions] = useState(null);
  const [formError, setFormError] = useState(false);
  const [laoding, setLoading] = useState(false);
  const { channelId, directoryId } = useParams();
  const [defaultConfigMapping, setDefaultConfigMapping] = useState({});

  const fetchConfigurationTypes = async () => {
    try {
      const res = await apiClient.get(`${SSO_URL}${FETCH_ENUM_TYPES}/configurationType`);
      const configOptions = Object.entries(res || {}).map(([key, value]) => ({ label: key, value: value }));
      setConfigurationKeys(configOptions);
    } catch (error) {
      console.log(error);
    }
  };



  const fetchTemplate = async () => {
    try {
      const payload = {
        applicationId,
        configName: configName.value,
        templateType: templateType.value,
      };
      const response = await apiClient.post(`${SSO_URL}${FETCH_APPLICATION_TEMPLATE}`, payload);
      const configDataVariables = response?.data || [];
      const parsedList = configDataVariables.map((item) => ({
        name: item.key,
        value: item.dataType === "object" ? JSON.stringify(item?.value || {}) : item.value,
        editable: true,
        key: generateShortUUID(),
        dataType: item.dataType,
      }));
      setDataList(parsedList);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddClick = () => {
    setDataList([...dataList, { name: "", value: "", key: generateShortUUID(), editable: false, dataType: "string" }]);
  };

  const handleRemoveClick = (key) => {
    setDataList((prev) => prev.filter((item) => item.key !== key));
  };

  const handleChange = (key, field, value) => {
    setDataList((prev) =>
      prev.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            [field]: value,
          };
        } else {
          return item;
        }
      })
    );
  };

  const handleSaveDefaultConfig = async () => {
    try {
      setLoading(true);
      const payload = {
        data: [],
        name: configName.value,
        templateType: templateType.value,
      };
      dataList.forEach((item) => {
        if (validateValue(String(item.value), item.dataType)) {
          const parsedValue = parseValue(String(item.value), item.dataType);
          payload.data.push({ key: item.name, value: parsedValue, dataType: item.dataType });
        } else {
          setFormError(true);
          return;
        }
      });
      const res = await apiClient.post(`${SSO_URL}${SAVE_DEFAULT_CONFIGURATION}/${applicationId}`, payload);
      reRender();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (defaultConfigMode) {
      handleSaveDefaultConfig();
    } else {
      handleSaveAppConfiguration();
    }
  };

  const handleSaveAppConfiguration = async () => {
    try {
      setLoading(true);
      const payload = {
        data: {},
        name: configName,
        type: defaultConfig?.name,
        applicationId: applicationId,
        channelId: channelId,
      };

      for (const item of dataList) {
        if (validateValue(String(item.value), item.dataType)) {
          const parsedValue = parseValue(String(item.value), item.dataType);
          if (defaultConfigMapping[item.name] !== parsedValue) {
            if (item.name) {
              payload.data[item.name] = parsedValue;
            }
          }
        } else {
          setFormError(true);
          return;
        }
      }
      setFormError(false);
      const params = {
        configurationId: configData?.id || null,
      };
      if (isEnv) {
        if (isDirectory) {
          payload.directoryId = directoryId;
        }
        const res = await apiClient.post(`${SSO_URL}${UPDATE_ENVIRONMENT_VARIABLES}`, payload, { params });
      } else {
        const res = await apiClient.post(`${SSO_URL}${UPDATE_CONFIGURATION_VARIABLES}`, payload, { params });
      }
      reRender();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!defaultConfigMode && configData) {
      const configDataVariables = configData.data || [];
      const parsedList = configDataVariables?.map((item) => ({
        name: item.key,
        value: getValueToPopulate(item.value),
        editable: true,
        key: generateShortUUID(),
        dataType: item.dataType,
      }));
      setDataList(parsedList);
      setConfigName(configData?.name || "");
      const mapping = {};
      defaultConfig?.data?.forEach((item) => {
        mapping[item.key] = item.value;
      });
      setDefaultConfigMapping(mapping || {});
    }
  }, [configData]);

  useEffect(() => {
    if (defaultConfigMode && templateType?.value && configName?.value) {
      fetchTemplate();
    }
  }, [configName?.value]);

  useEffect(() => {
    if (defaultConfigMode) {
      fetchConfigurationTypes();
    }
  }, []);

  useEffect(() => {
    if (templateType?.value === "APPLICATION") {
      setConfigName(null);
      setConfigNameOptions([{ label: "ENV", value: "ENV" }]);
    }
    if (templateType?.value === "CHANNEL") {
      setConfigName(null);
      setConfigNameOptions(configurationKeys.filter((item) => item.value !== "ENV"));
    }
  }, [templateType?.value]);

  return (
    <>
      <div className="variable-list ">
        {defaultConfigMode && (
          <div className="standardinput">
            <label>Template Type</label>
            <div className="forselect-input">
              <Select
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 5,
                  colors: {
                    ...theme.colors,
                    primary50: "#ca373c50",
                    primary25: "#ca373c50",
                    primary: "#ca373c70",
                    danger: "#000000",
                    neutral30: "#00000050",
                  },
                })}
                value={templateType}
                options={templateTypeOptions}
                onChange={(option) => setTemplateType(option)}
              />
            </div>
          </div>
        )}
        {defaultConfigMode && (
          <div className="standardinput">
            <label>Configuration Name</label>
            <div className="forselect-input">
              <Select
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 5,
                  colors: {
                    ...theme.colors,
                    primary50: "#ca373c50",
                    primary25: "#ca373c50",
                    primary: "#ca373c70",
                    danger: "#000000",
                    neutral30: "#00000050",
                  },
                })}
                value={configName}
                options={configNameOptions}
                onChange={(option) => setConfigName(option)}
              />
            </div>
          </div>
        )}

        {!defaultConfigMode && !isEnv && (
          <div className="standardinput">
            <label>Configuration Name</label>
            <input
              type="text"
              value={configName}
              className="input form-control"
              onChange={(e) => setConfigName(e.target.value)}
            />
          </div>
        )}

        <div className="parent">
          <div>Configuration variable key</div>
          <div>Configuration variable value</div>
        </div>

        {(!defaultConfigMode || (templateType?.value && configName?.value)) &&
          dataList.map((item, index) => (
            <div className="child" key={item.key}>
              <div className="standardinput">
                <input
                  type="text"
                  disabled={defaultConfigMode === false ? true : item.editable}
                  value={item.name}
                  className="input form-control"
                  onChange={(e) => handleChange(item.key, "name", e.target.value)}
                />
              </div>
              <div className="standardinput">
                <input
                  type="text"
                  disabled={item.editable}
                  value={item.value}
                  className="input form-control"
                  onChange={(e) => handleChange(item.key, "value", e.target.value)}
                />
              </div>
              <div className="standardinput">
                <div className="forselect-input">
                  <Select
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 5,
                      colors: {
                        ...theme.colors,
                        primary50: "#ca373c50",
                        primary25: "#ca373c50",
                        primary: "#ca373c70",
                        danger: "#000000",
                        neutral30: "#00000050",
                      },
                    })}
                    isDisabled={!defaultConfigMode}
                    value={dataTypeOptions.find((type) => type.value === item.dataType)}
                    options={dataTypeOptions}
                    onChange={(option) => handleChange(item.key, "dataType", option.value)}
                  />
                </div>
              </div>
              <div className="edit-remove">
                <button onClick={() => handleChange(item.key, "editable", false)}>Edit</button>
                {defaultConfigMode && (
                  <button className="remove" onClick={() => handleRemoveClick(item.key)}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        <div className="add-new mt-3 mb-5">
          {defaultConfigMode && (
            <button className="DefaultRedBtn" onClick={handleAddClick}>
              <PlusIcon />
              Add Variable
            </button>
          )}
        </div>
        {formError && <p className="error">Data and Data type are not matching</p>}
        <div className="footerbtn w-100 mt-5">
          <button className="cancel"> Cancel </button>
          {!laoding ? (
            <button className="save" onClick={handleSave}>
              Save
            </button>
          ) : (
            <button className="loading save">
              {" "}
              <svg viewBox="25 25 50 50">
                {" "}
                <circle r="20" cy="50" cx="50"></circle>{" "}
              </svg>{" "}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateApplicationConfig;
